/**
 * Email Domains API Service
 * Handles all API interactions for email domain management
 */

import { apiRequest } from '../client';

export interface EmailDomain {
  id: string;
  organisation_id: string;
  organisation_name?: string;
  domain: string;
  is_verified: boolean;
  verification_status: 'pending' | 'success' | 'failed';
  cloudflare_zone_id: string | null;
  cloudflare_nameservers: string[] | null;
  cloudflare_zone_status: 'pending' | 'active' | 'moved' | null;
  dns_records_synced: boolean;
  dns_management_mode: 'cloudflare' | 'manual';
  default_from_email: string;
  default_from_name: string;
  verification_token: string | null;
  dkim_tokens: string[] | null;
  // MAIL FROM fields
  mail_from_domain: string | null;
  mail_from_status: 'not_configured' | 'pending' | 'success' | 'failed';
  mail_from_mx_value: string | null;
  mail_from_configured_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface AddDomainRequest {
  organisationId: string;
  domain: string;
  defaultFromEmail: string;
  defaultFromName: string;
}

export interface ImportDomainRequest extends AddDomainRequest {}

export interface AddSubdomainRequest {
  subdomain: string;
  ipAddress: string;
  proxied: boolean;
}

export const EmailDomainsService = {
  /**
   * Fetch all email domains with optional filters
   */
  fetchDomains: async (filters?: {
    organisation_id?: string;
    is_verified?: boolean;
    dns_records_synced?: boolean;
    search?: string;
  }): Promise<EmailDomain[]> => {
    try {
      // Build query string
      const queryParams = new URLSearchParams();
      if (filters?.organisation_id) queryParams.set('organisation_id', filters.organisation_id);
      if (filters?.is_verified !== undefined) queryParams.set('is_verified', String(filters.is_verified));
      if (filters?.dns_records_synced !== undefined) queryParams.set('dns_records_synced', String(filters.dns_records_synced));
      if (filters?.search) queryParams.set('search', filters.search);

      const queryString = queryParams.toString();
      const endpoint = `/api/admin/email/domains${queryString ? `?${queryString}` : ''}`;

      const response = await apiRequest<{ success: boolean; domains: EmailDomain[] }>({
        method: 'GET',
        url: endpoint
      });

      if (!response.success || !response.domains) {
        throw new Error('Failed to fetch domains');
      }

      return response.domains;
    } catch (error) {
      console.error('Error fetching domains:', error);
      throw error;
    }
  },

  /**
   * Get a single domain by ID
   */
  getDomain: async (domainId: string): Promise<EmailDomain> => {
    try {
      const response = await apiRequest<{ success: boolean; domain: EmailDomain }>({
        method: 'GET',
        url: `/api/admin/email/domains/${domainId}`
      });

      if (!response.success || !response.domain) {
        throw new Error('Failed to fetch domain');
      }

      return response.domain;
    } catch (error) {
      console.error('Error fetching domain:', error);
      throw error;
    }
  },

  /**
   * Register a new domain (creates Cloudflare zone + registers with SES)
   */
  addDomain: async (data: AddDomainRequest): Promise<{
    domain: EmailDomain;
    nameservers: string[];
    instructions: string;
  }> => {
    try {
      const response = await apiRequest<{
        success: boolean;
        domain: EmailDomain;
        nameservers: string[];
        message: string;
      }>({
        method: 'POST',
        url: '/api/admin/email/domains',
        data: {
          organisation_id: data.organisationId,
          domain: data.domain,
          default_from_email: data.defaultFromEmail,
          default_from_name: data.defaultFromName
        }
      });

      if (!response.success || !response.domain) {
        throw new Error(response.message || 'Failed to add domain');
      }

      return {
        domain: response.domain,
        nameservers: response.nameservers || [],
        instructions: response.message || ''
      };
    } catch (error) {
      console.error('Error adding domain:', error);
      throw error;
    }
  },

  /**
   * Import existing Cloudflare domain
   */
  importDomain: async (data: ImportDomainRequest): Promise<{
    domain: EmailDomain;
    nameservers: string[];
    instructions: string;
  }> => {
    try {
      const response = await apiRequest<{
        success: boolean;
        domain: EmailDomain;
        nameservers: string[];
        message: string;
      }>({
        method: 'POST',
        url: '/api/admin/email/domains/import-existing',
        data: {
          organisation_id: data.organisationId,
          domain: data.domain,
          default_from_email: data.defaultFromEmail,
          default_from_name: data.defaultFromName
        }
      });

      if (!response.success || !response.domain) {
        throw new Error(response.message || 'Failed to import domain');
      }

      return {
        domain: response.domain,
        nameservers: response.nameservers || [],
        instructions: response.message || ''
      };
    } catch (error) {
      console.error('Error importing domain:', error);
      throw error;
    }
  },

  /**
   * Verify nameservers for a domain
   */
  verifyNameservers: async (domainId: string): Promise<{
    status: string;
    message: string;
  }> => {
    try {
      const response = await apiRequest<{
        success: boolean;
        status: string;
        message: string;
      }>({
        method: 'POST',
        url: `/api/admin/email/domains/${domainId}/verify-nameservers`
      });

      if (!response.success) {
        throw new Error(response.message || 'Failed to verify nameservers');
      }

      return {
        status: response.status,
        message: response.message
      };
    } catch (error) {
      console.error('Error verifying nameservers:', error);
      throw error;
    }
  },

  /**
   * Sync SES DNS records to Cloudflare
   */
  syncSESRecords: async (domainId: string): Promise<{
    success: boolean;
    records_created: number;
  }> => {
    try {
      const response = await apiRequest<{
        success: boolean;
        records_created: number;
        message: string;
      }>({
        method: 'POST',
        url: `/api/admin/email/domains/${domainId}/sync-ses-records`
      });

      if (!response.success) {
        throw new Error(response.message || 'Failed to sync SES records');
      }

      return {
        success: response.success,
        records_created: response.records_created || 0
      };
    } catch (error) {
      console.error('Error syncing SES records:', error);
      throw error;
    }
  },

  /**
   * Check SES verification status
   */
  checkVerification: async (domainId: string): Promise<{
    is_verified: boolean;
    verification_status: string;
  }> => {
    try {
      const response = await apiRequest<{
        success: boolean;
        is_verified: boolean;
        verification_status: string;
        message: string;
      }>({
        method: 'POST',
        url: `/api/admin/email/domains/${domainId}/check-verification`
      });

      if (!response.success) {
        throw new Error(response.message || 'Failed to check verification');
      }

      return {
        is_verified: response.is_verified,
        verification_status: response.verification_status
      };
    } catch (error) {
      console.error('Error checking verification:', error);
      throw error;
    }
  },

  /**
   * Add subdomain A record
   */
  addSubdomain: async (domainId: string, data: AddSubdomainRequest): Promise<{
    success: boolean;
    record: any;
  }> => {
    try {
      const response = await apiRequest<{
        success: boolean;
        record: any;
        message: string;
      }>({
        method: 'POST',
        url: `/api/admin/email/domains/${domainId}/subdomain`,
        data
      });

      if (!response.success) {
        throw new Error(response.message || 'Failed to add subdomain');
      }

      return {
        success: response.success,
        record: response.record
      };
    } catch (error) {
      console.error('Error adding subdomain:', error);
      throw error;
    }
  },

  /**
   * Update domain default sender information
   */
  updateDomain: async (domainId: string, data: {
    default_from_email?: string;
    default_from_name?: string;
  }): Promise<EmailDomain> => {
    try {
      const response = await apiRequest<{
        success: boolean;
        domain: EmailDomain;
        message: string;
      }>({
        method: 'PUT',
        url: `/api/admin/email/domains/${domainId}`,
        data: {
          default_from_email: data.default_from_email,
          default_from_name: data.default_from_name
        }
      });

      if (!response.success || !response.domain) {
        throw new Error(response.message || 'Failed to update domain');
      }

      return response.domain;
    } catch (error) {
      console.error('Error updating domain:', error);
      throw error;
    }
  },

  /**
   * Delete a domain
   */
  deleteDomain: async (domainId: string): Promise<void> => {
    try {
      const response = await apiRequest<{ success: boolean; message: string }>({
        method: 'DELETE',
        url: `/api/admin/email/domains/${domainId}`
      });

      if (!response.success) {
        throw new Error(response.message || 'Failed to delete domain');
      }
    } catch (error) {
      console.error('Error deleting domain:', error);
      throw error;
    }
  },

  /**
   * Configure custom MAIL FROM domain
   */
  configureMailFrom: async (domainId: string, subdomain: string = 'chef'): Promise<{
    success: boolean;
    mail_from_domain: string;
    dns_records: any[];
    message: string;
  }> => {
    try {
      const response = await apiRequest<{
        success: boolean;
        data: {
          mail_from_domain: string;
          dns_records: any[];
        };
        message: string;
      }>({
        method: 'POST',
        url: `/api/admin/email/domains/${domainId}/configure-mail-from`,
        data: { subdomain }
      });

      if (!response.success) {
        throw new Error(response.message || 'Failed to configure MAIL FROM');
      }

      return {
        success: response.success,
        mail_from_domain: response.data.mail_from_domain,
        dns_records: response.data.dns_records,
        message: response.message
      };
    } catch (error) {
      console.error('Error configuring MAIL FROM:', error);
      throw error;
    }
  },

  /**
   * Sync MAIL FROM DNS records to Cloudflare
   */
  syncMailFromDNS: async (domainId: string): Promise<{
    success: boolean;
    records_created: number;
    message: string;
  }> => {
    try {
      const response = await apiRequest<{
        success: boolean;
        data: {
          records_created: number;
        };
        message: string;
      }>({
        method: 'POST',
        url: `/api/admin/email/domains/${domainId}/sync-mail-from-dns`
      });

      if (!response.success) {
        throw new Error(response.message || 'Failed to sync MAIL FROM DNS');
      }

      return {
        success: response.success,
        records_created: response.data.records_created,
        message: response.message
      };
    } catch (error) {
      console.error('Error syncing MAIL FROM DNS:', error);
      throw error;
    }
  },

  /**
   * Check MAIL FROM verification status
   */
  checkMailFromStatus: async (domainId: string): Promise<{
    success: boolean;
    mail_from_domain: string;
    status: string;
    verified: boolean;
    message: string;
  }> => {
    try {
      const response = await apiRequest<{
        success: boolean;
        data: {
          mail_from_domain: string;
          status: string;
          verified: boolean;
        };
        message: string;
      }>({
        method: 'POST',
        url: `/api/admin/email/domains/${domainId}/check-mail-from`
      });

      if (!response.success) {
        throw new Error(response.message || 'Failed to check MAIL FROM status');
      }

      return {
        success: response.success,
        mail_from_domain: response.data.mail_from_domain,
        status: response.data.status,
        verified: response.data.verified,
        message: response.message
      };
    } catch (error) {
      console.error('Error checking MAIL FROM status:', error);
      throw error;
    }
  },

  /**
   * Register domain for manual DNS management (customer manages DNS)
   */
  registerDomainManual: async (data: AddDomainRequest): Promise<{
    domain: EmailDomain;
    dns_records: any[];
    formatted_text: string;
  }> => {
    try {
      const response = await apiRequest<{
        success: boolean;
        data: {
          domain: EmailDomain;
          dns_records: any[];
          formatted_text: string;
        };
        message: string;
      }>({
        method: 'POST',
        url: '/api/admin/email/domains/manual',
        data: {
          organisation_id: data.organisationId,
          domain: data.domain,
          default_from_email: data.defaultFromEmail,
          default_from_name: data.defaultFromName
        }
      });

      if (!response.success || !response.data) {
        throw new Error(response.message || 'Failed to register domain for manual DNS');
      }

      return response.data;
    } catch (error) {
      console.error('Error registering domain for manual DNS:', error);
      throw error;
    }
  },

  /**
   * Convert existing Cloudflare domain to manual DNS mode
   */
  convertToManualDNS: async (domainId: string): Promise<{
    domain: EmailDomain;
    dns_records: any[];
    formatted_text: string;
  }> => {
    try {
      const response = await apiRequest<{
        success: boolean;
        data: {
          domain: EmailDomain;
          dns_records: any[];
          formatted_text: string;
        };
        message: string;
      }>({
        method: 'POST',
        url: `/api/admin/email/domains/${domainId}/convert-to-manual`
      });

      if (!response.success || !response.data) {
        throw new Error(response.message || 'Failed to convert domain to manual DNS');
      }

      return response.data;
    } catch (error) {
      console.error('Error converting domain to manual DNS:', error);
      throw error;
    }
  },

  /**
   * Get DNS instructions for any domain (manual or Cloudflare-managed)
   */
  getDNSInstructions: async (domainId: string): Promise<{
    domain: string;
    dns_records: any[];
    formatted_text: string;
  }> => {
    try {
      const response = await apiRequest<{
        success: boolean;
        data: {
          domain: string;
          dns_records: any[];
          formatted_text: string;
        };
        message: string;
      }>({
        method: 'GET',
        url: `/api/admin/email/domains/${domainId}/dns-instructions`
      });

      if (!response.success || !response.data) {
        throw new Error(response.message || 'Failed to get DNS instructions');
      }

      return response.data;
    } catch (error) {
      console.error('Error getting DNS instructions:', error);
      throw error;
    }
  }
};
